import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import useSEO from '../../hooks/useSEO';

export default function ComoLoHacemos() {
  useSEO({
    title: 'Cómo lo Hacemos - Proceso de Automatización SEO con IA | SEOgenia',
    description: 'Descubre nuestro proceso de automatización SEO con IA Generativa: análisis técnico, informe de modificaciones, actuación automatizada y panel de seguimiento 24/7. Metodología probada para resultados excepcionales.',
    keywords: 'proceso SEO, automatización SEO, metodología SEO, análisis técnico SEO, auditoría SEO, implementación SEO, seguimiento SEO, panel SEO, IA generativa SEO',
    canonical: 'https://seogenia.es/como-lo-hacemos',
    ogType: 'website',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'Proceso de Automatización SEO con IA Generativa',
      description: 'Metodología completa de 4 pasos para optimizar tu sitio web con IA: análisis, planificación, implementación y seguimiento continuo.',
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Análisis del Sitio Web',
          text: 'Auditoría técnica completa de tu sitio web actual, incluyendo arquitectura, Core Web Vitals, Schema Markup, enlaces y contenido.',
          itemListElement: [
            {
              '@type': 'HowToDirection',
              text: 'Análisis de arquitectura y estructura del sitio'
            },
            {
              '@type': 'HowToDirection',
              text: 'Evaluación de Core Web Vitals y rendimiento'
            },
            {
              '@type': 'HowToDirection',
              text: 'Revisión de Schema Markup existente'
            }
          ]
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'Informe de Modificaciones',
          text: 'Documento detallado con todas las optimizaciones planificadas, priorización por impacto y roadmap de implementación.',
          itemListElement: [
            {
              '@type': 'HowToDirection',
              text: 'Priorización de acciones por impacto SEO'
            },
            {
              '@type': 'HowToDirection',
              text: 'Roadmap de implementación detallado'
            },
            {
              '@type': 'HowToDirection',
              text: 'Estimación de resultados esperados'
            }
          ]
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'Actuación Automatizada',
          text: 'Implementación automática de todas las optimizaciones con IA: Schema Markup, velocidad, PWA, optimización para agentes IA y enlaces internos.',
          itemListElement: [
            {
              '@type': 'HowToDirection',
              text: 'Implementación de Schema Markup avanzado'
            },
            {
              '@type': 'HowToDirection',
              text: 'Optimización de velocidad y caché'
            },
            {
              '@type': 'HowToDirection',
              text: 'Configuración de Progressive Web App'
            }
          ]
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'Panel de Seguimiento',
          text: 'Acceso 24/7 a tu panel personalizado con monitorización en tiempo real, gráficos de evolución y alertas automáticas.',
          itemListElement: [
            {
              '@type': 'HowToDirection',
              text: 'Monitorización en tiempo real de métricas'
            },
            {
              '@type': 'HowToDirection',
              text: 'Gráficos de evolución de rankings'
            },
            {
              '@type': 'HowToDirection',
              text: 'Informes mensuales detallados'
            }
          ]
        }
      ],
      totalTime: 'P30D'
    }
  });

  const heroRef = useRef(null);
  const processRef = useRef(null);
  const benefitsRef = useRef(null);
  const ctaRef = useRef(null);

  const isHeroInView = useInView(heroRef, { once: true });
  const isProcessInView = useInView(processRef, { once: true, margin: "-100px" });
  const isBenefitsInView = useInView(benefitsRef, { once: true, margin: "-100px" });
  const isCtaInView = useInView(ctaRef, { once: true, margin: "-100px" });

  const steps = [
    {
      number: '01',
      title: 'Análisis del Sitio Web',
      icon: 'ri-search-line',
      description: 'Auditoría técnica completa de tu sitio web actual',
      details: [
        'Análisis de arquitectura y estructura',
        'Evaluación de Core Web Vitals',
        'Revisión de Schema Markup existente',
        'Análisis de enlaces internos y externos',
        'Evaluación de contenido y keywords',
        'Identificación de oportunidades de mejora'
      ],
      color: 'cyan-500',
      duration: '3-5 días',
      deliverable: 'Informe de auditoría técnica completo'
    },
    {
      number: '02',
      title: 'Informe de Modificaciones',
      icon: 'ri-file-list-3-line',
      description: 'Documento detallado con todas las optimizaciones planificadas',
      details: [
        'Priorización de acciones por impacto',
        'Roadmap de implementación',
        'Estimación de resultados esperados',
        'Cronograma de ejecución',
        'Recursos necesarios',
        'KPIs y métricas de seguimiento'
      ],
      color: 'purple-500',
      duration: '2-3 días',
      deliverable: 'Plan de acción estratégico'
    },
    {
      number: '03',
      title: 'Actuación Automatizada',
      icon: 'ri-settings-3-line',
      description: 'Implementación de todas las optimizaciones con IA',
      details: [
        'Implementación de Schema Markup',
        'Optimización de velocidad y caché',
        'Configuración de PWA',
        'Optimización para agentes IA',
        'Mejora de enlaces internos',
        'Actualización continua automática'
      ],
      color: 'green-500',
      duration: '5-7 días',
      deliverable: 'Sitio web completamente optimizado'
    },
    {
      number: '04',
      title: 'Panel de Seguimiento',
      icon: 'ri-dashboard-3-line',
      description: 'Acceso 24/7 a tu panel personalizado de métricas',
      details: [
        'Monitorización en tiempo real',
        'Gráficos de evolución de rankings',
        'Análisis de tráfico orgánico',
        'Core Web Vitals actualizados',
        'Alertas automáticas',
        'Informes mensuales detallados'
      ],
      color: 'orange-500',
      duration: 'Continuo',
      deliverable: 'Dashboard personalizado con acceso permanente'
    }
  ];

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Ahorro de Tiempo',
      description: 'Automatización completa que te ahorra cientos de horas de trabajo manual en optimización SEO.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'ROI Garantizado',
      description: 'Inversión que se recupera rápidamente con el aumento de tráfico orgánico y conversiones.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Resultados Medibles',
      description: 'Métricas claras y transparentes que demuestran el impacto real de cada optimización.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Seguridad Total',
      description: 'Implementaciones seguras que no afectan negativamente tu sitio web actual.'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Actualización Continua',
      description: 'Tu sitio se mantiene siempre actualizado con las últimas mejores prácticas de SEO.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Soporte Experto',
      description: 'Equipo de especialistas disponible para resolver cualquier duda o necesidad.'
    }
  ];

  const faqs = [
    {
      question: '¿Cuánto tiempo tarda el proceso completo?',
      answer: 'El proceso inicial de análisis, planificación e implementación toma entre 10-15 días. Después, el panel de seguimiento funciona de forma continua 24/7 para mantener tu sitio optimizado.'
    },
    {
      question: '¿Qué pasa si mi sitio ya tiene optimizaciones SEO?',
      answer: 'Perfecto. Analizamos tu trabajo previo y lo complementamos con nuestras optimizaciones avanzadas de IA. No perdemos ninguna mejora que ya hayas implementado.'
    },
    {
      question: '¿Necesito conocimientos técnicos para usar el panel?',
      answer: 'No. El panel está diseñado para ser intuitivo y fácil de usar. Además, incluimos tutoriales y soporte para que aproveches al máximo todas las funcionalidades.'
    },
    {
      question: '¿Qué diferencia hay con otras agencias SEO?',
      answer: 'Usamos IA Generativa para automatizar procesos que otras agencias hacen manualmente. Esto nos permite ser más rápidos, precisos y ofrecer seguimiento continuo a un coste mucho menor.'
    },
    {
      question: '¿Puedo cancelar el servicio en cualquier momento?',
      answer: 'Sí. No hay permanencia. Puedes cancelar cuando quieras, aunque estamos seguros de que los resultados te convencerán de quedarte.'
    },
    {
      question: '¿Qué garantías ofrecen?',
      answer: 'Garantizamos mejoras medibles en Core Web Vitals, implementación correcta de Schema Markup y optimización técnica completa. Si no ves resultados en 90 días, te devolvemos tu inversión.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-16 sm:pt-40 sm:pb-24 bg-cyan-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/375aadca02b7c239a5646b2bde5dcb96.jpg')] opacity-5 bg-cover bg-center"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-100 rounded-full mb-6">
              <i className="ri-settings-3-line text-cyan-600"></i>
              <span className="text-sm font-medium text-gray-700">Metodología Probada</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-black mb-6">
              Cómo <span className="text-cyan-500">Trabajamos</span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
              Un proceso claro, transparente y automatizado que garantiza resultados excepcionales en tu posicionamiento web
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/#contacto" 
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-cyan-500 text-white rounded-full font-bold text-base sm:text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Comenzar Ahora
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a 
                href="#proceso" 
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white text-gray-800 rounded-full font-bold text-base sm:text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 border-2 border-gray-200 whitespace-nowrap cursor-pointer"
              >
                Ver Proceso
                <i className="ri-arrow-down-line"></i>
              </a>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 sm:mt-20"
          >
            {[
              { number: '10-15', label: 'Días de Implementación', icon: 'ri-calendar-line' },
              { number: '24/7', label: 'Monitorización Continua', icon: 'ri-time-line' },
              { number: '100%', label: 'Automatización IA', icon: 'ri-robot-line' },
              { number: '90', label: 'Días de Garantía', icon: 'ri-shield-check-line' }
            ].map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
                <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <i className={`${stat.icon} text-2xl text-white`}></i>
                </div>
                <div className="text-3xl font-bold text-cyan-500 mb-1">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section id="proceso" ref={processRef} className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isProcessInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="mb-12 sm:mb-16 text-center"
          >
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-1 w-16 sm:w-20 bg-cyan-500 rounded-full"></div>
              <span className="text-base sm:text-lg font-medium text-cyan-500">Nuestro Proceso</span>
              <div className="h-1 w-16 sm:w-20 bg-cyan-500 rounded-full"></div>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">
              4 Pasos hacia el <span className="text-cyan-500">Éxito SEO</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Cada paso está diseñado para maximizar resultados y garantizar transparencia total
            </p>
          </motion.div>

          <div className="space-y-12 sm:space-y-16">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={isProcessInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
              >
                {/* Step Number & Icon */}
                <div className="flex-shrink-0">
                  <div className={`w-32 h-32 rounded-3xl bg-${step.color} flex items-center justify-center shadow-2xl relative`}>
                    <div className="absolute inset-0 bg-black/10 rounded-3xl"></div>
                    <i className={`${step.icon} text-5xl text-white relative z-10`}></i>
                    <div className="absolute -top-4 -right-4 w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                      <span className="text-2xl font-bold text-cyan-500">
                        {step.number}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Step Content */}
                <div className="flex-1 bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3">{step.title}</h3>
                  <p className="text-gray-600 mb-6 text-base sm:text-lg">{step.description}</p>

                  <div className="grid sm:grid-cols-2 gap-3 mb-6">
                    {step.details.map((detail, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className={`w-5 h-5 rounded-md flex items-center justify-center bg-${step.color} flex-shrink-0 mt-0.5`}>
                          <i className="ri-check-line text-white text-xs"></i>
                        </div>
                        <span className="text-sm text-gray-700">{detail}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <i className="ri-time-line text-cyan-500"></i>
                      <span className="text-sm font-medium text-gray-700">Duración: {step.duration}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-file-text-line text-pink-500"></i>
                      <span className="text-sm font-medium text-gray-700">{step.deliverable}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isBenefitsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="mb-12 sm:mb-16 text-center"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">
              Beneficios de Nuestro <span className="text-cyan-500">Proceso</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Por qué nuestra metodología es diferente y más efectiva
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isBenefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-cyan-500 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isProcessInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="mb-12 sm:mb-16 text-center"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">
              Preguntas <span className="text-cyan-500">Frecuentes</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              Resolvemos tus dudas sobre nuestro proceso
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isProcessInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden group hover:shadow-lg transition-all duration-300"
              >
                <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between font-semibold text-gray-800 hover:text-cyan-500 transition-colors">
                  <span className="text-base sm:text-lg">{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform duration-300"></i>
                </summary>
                <div className="px-6 pb-5 text-gray-600 text-sm sm:text-base leading-relaxed border-t border-gray-100 pt-4">
                  {faq.answer}
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-16 sm:py-24 bg-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isCtaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="bg-purple-600 rounded-3xl p-8 sm:p-12 text-center text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                ¿Listo para Transformar tu SEO?
              </h2>
              <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto">
                Comienza hoy con una auditoría gratuita y descubre el potencial de tu sitio web
              </p>
              <Link 
                to="/#contacto"
                className="inline-block px-8 py-4 bg-cyan-500 text-white rounded-full font-medium text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                Solicitar Consulta Gratuita
              </Link>
            </div>
          </motion.div>

          {/* Related Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isCtaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12 sm:mt-16"
          >
            <h3 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-8">
              Servicios Relacionados
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: 'Arquitectura Semántica',
                  description: 'Estructura optimizada para buscadores y usuarios',
                  link: '/servicios/arquitectura-semantica',
                  icon: 'ri-code-box-line'
                },
                {
                  title: 'Schema Markup Avanzado',
                  description: 'Datos estructurados para destacar en resultados',
                  link: '/servicios/schema-markup-avanzado',
                  icon: 'ri-file-code-line'
                },
                {
                  title: 'SEO para Agentes IA',
                  description: 'Optimización para ChatGPT, Gemini y más',
                  link: '/servicios/seo-agentes-ia',
                  icon: 'ri-robot-line'
                }
              ].map((service, index) => (
                <Link
                  key={index}
                  to={service.link}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-2xl text-white`}></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-800 mb-2">{service.title}</h4>
                  <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                  <span className="text-cyan-500 font-medium text-sm inline-flex items-center gap-1">
                    Ver más <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
